import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Fade, TextField, InputAdornment, Button, Paper, IconButton, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Pagination from '../../common/Pagination';
import LogoLoader from '../../common/LogoLoader';
import * as XLSX from 'xlsx-js-style';

function DebtorsListReport() {
  useSessionValidation();

  const { companyId, branchId, token, companyName } = useAuth();

  const today = new Date().toISOString().split('T')[0];

  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [toDate, setToDate] = useState(today);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [expandedCustomers, setExpandedCustomers] = useState({});

  useEffect(() => { setMounted(true); }, []);

  const fetchDebtors = async () => {
    if (!companyId || !token) return;
    setLoading(true);
    try {
      const response = await fetchData(
        'ReportApi',
        `/DebtorsList/${companyId}/${branchId || 0}/${toDate}/${toDate}`,
        token
      );
      const rows = response?.response_data || response || [];
      setData(Array.isArray(rows) ? rows : []);
    } catch (e) {
      console.error('Error fetching debtors list:', e);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && token) fetchDebtors();
  }, [companyId, token]); // eslint-disable-line

  const fmt = (n) =>
    parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // Handle ISO format with time (2026-07-07T00:00:00 or 2026-07-07)
    const d = dateStr.split('T')[0];
    const parts = d.split('-');
    if (parts.length !== 3) return '';
    // If first part is 4 digits, it's YYYY-MM-DD
    if (parts[0].length === 4) {
      const [year, month, day] = parts;
      return `${parseInt(day)}/${parseInt(month)}/${year}`;
    }
    // DD-MM-YYYY format
    if (parts[0].length <= 2) {
      const [day, month, year] = parts;
      return `${parseInt(day)}/${parseInt(month)}/${year}`;
    }
    return '';
  };

  const filteredData = useMemo(() =>
    data.filter(item =>
      !searchTerm ||
      (item.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(item.Billno || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.billdate || '').toLowerCase().includes(searchTerm.toLowerCase())
    ),
    [data, searchTerm]
  );

  // Group data by customer name
  const groupedData = useMemo(() => {
    const groups = {};
    filteredData.forEach(item => {
      const name = item.name || 'Unknown';
      if (!groups[name]) {
        groups[name] = { name, items: [], totalAmount: 0, maxDays: 0 };
      }
      groups[name].items.push(item);
      groups[name].totalAmount += Math.abs(parseFloat(item.amount || 0));
      groups[name].maxDays = Math.max(groups[name].maxDays, item.days || 0);
    });
    // Sort by total amount descending
    return Object.values(groups).sort((a, b) => b.totalAmount - a.totalAmount);
  }, [filteredData]);

  const totalCustomers = groupedData.length;
  const totalPages = Math.ceil(totalCustomers / itemsPerPage);
  const paginatedGroups = groupedData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const totalAmount = useMemo(() =>
    filteredData.reduce((sum, item) => sum + Math.abs(parseFloat(item.amount || 0)), 0),
    [filteredData]
  );

  const totalBills = filteredData.length;

  // Expand/collapse logic
  const toggleCustomer = useCallback((name) => {
    setExpandedCustomers(prev => ({ ...prev, [name]: !prev[name] }));
  }, []);

  const expandAll = useCallback(() => {
    const all = {};
    paginatedGroups.forEach(g => { all[g.name] = true; });
    setExpandedCustomers(prev => ({ ...prev, ...all }));
  }, [paginatedGroups]);

  const collapseAll = useCallback(() => {
    setExpandedCustomers({});
  }, []);

  // Auto-expand all when data loads
  useEffect(() => {
    if (groupedData.length > 0) {
      const all = {};
      groupedData.forEach(g => { all[g.name] = true; });
      setExpandedCustomers(all);
    }
  }, [groupedData]);

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    const COLS = 6;

    const rows = [];
    rows.push([companyName || 'Debtors List', ...Array(COLS - 1).fill('')]);
    rows.push(['Debtors List (Grouped by Customer)', ...Array(COLS - 1).fill('')]);
    rows.push([`As of : ${toDate}`, ...Array(COLS - 1).fill('')]);
    rows.push(Array(COLS).fill(''));
    rows.push(['#', 'Name / Bill No', 'Bill No', 'Bill Date', 'Amount', 'Days']);

    let slno = 0;
    groupedData.forEach(group => {
      slno++;
      // Customer header row
      rows.push([slno, `${group.name} (${group.items.length} bills)`, '', '', group.totalAmount, group.maxDays]);
      // Bill detail rows
      group.items.forEach(item => {
        rows.push(['', '', item.Billno || '', formatDate(item.billdate), Math.abs(parseFloat(item.amount || 0)), item.days || 0]);
      });
    });

    rows.push([totalCustomers, `Total (${totalBills} bills)`, '', '', totalAmount, '']);

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 6 }, { wch: 32 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 8 }];
    ws['!rows'] = [{ hpt: 22 }, { hpt: 18 }, { hpt: 16 }, { hpt: 6 }, { hpt: 20 }];
    ws['!merges'] = [0, 1, 2, 3].map(r => ({ s: { r, c: 0 }, e: { r, c: COLS - 1 } }));

    const infoS = (sz, bold, color = '5c3d1e') => ({
      font: { bold, sz, color: { rgb: color } },
      alignment: { horizontal: 'center', vertical: 'center' },
      fill: { patternType: 'solid', fgColor: { rgb: 'FFF8EE' } },
    });
    ws['A1'].s = infoS(16, true);
    ws['A2'].s = infoS(13, true, '2d3748');
    ws['A3'].s = infoS(11, false, '444444');

    const hdrS = {
      font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } },
      fill: { patternType: 'solid', fgColor: { rgb: '5c3d1e' } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        top: { style: 'medium', color: { rgb: '3b2410' } },
        bottom: { style: 'medium', color: { rgb: '3b2410' } },
        left: { style: 'thin', color: { rgb: '3b2410' } },
        right: { style: 'thin', color: { rgb: '3b2410' } },
      },
    };
    for (let c = 0; c < COLS; c++) {
      const addr = XLSX.utils.encode_cell({ r: 4, c });
      if (ws[addr]) ws[addr].s = hdrS;
    }

    XLSX.utils.book_append_sheet(wb, ws, 'Debtors');
    XLSX.writeFile(wb, `DebtorsList_${new Date().toISOString().split('T')[0]}.xlsx`, { bookSST: false, cellStyles: true });
  };

  const headerCell = (align = 'left') => ({
    color: 'white', fontWeight: '600', fontSize: '11px',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    padding: '4px 10px', textAlign: align,
    whiteSpace: 'nowrap', backgroundColor: '#5c3d1e',
  });

  const bodyCell = (align = 'left', bold = false) => ({
    fontSize: '12px', padding: '6px 10px',
    color: '#2d3748', whiteSpace: 'nowrap',
    textAlign: align, fontWeight: bold ? '600' : '400',
  });

  if (!mounted) return null;

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{ minWidth: '100%', background: '#fdf6ec', padding: '10px', position: 'relative' }}>
        {loading && <LogoLoader open={loading} message="Loading Debtors List..." />}

        <div style={{
          margin: '0 auto', background: 'white', borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px',
          color: 'black', fontFamily: 'Times New Roman', overflow: 'hidden',
        }}>
          {/* Title */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
            <h4 style={{ fontSize: '17px', fontWeight: '700', margin: 0, letterSpacing: '-0.3px', fontFamily: 'Times New Roman', textTransform: 'uppercase' }}>
              <span style={{ color: 'black' }}>📋 Debtors List</span>
            </h4>
            {/* Summary badges */}
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span style={{ fontSize: '11px', background: '#e3f2fd', color: '#1565c0', padding: '3px 8px', borderRadius: '12px', fontWeight: '600' }}>
                {totalCustomers} Customers
              </span>
              <span style={{ fontSize: '11px', background: '#fff3e0', color: '#e65100', padding: '3px 8px', borderRadius: '12px', fontWeight: '600' }}>
                {totalBills} Bills
              </span>
              <span style={{ fontSize: '11px', background: '#fce4ec', color: '#c62828', padding: '3px 8px', borderRadius: '12px', fontWeight: '600' }}>
                ₹ {fmt(totalAmount)}
              </span>
            </div>
          </div>

          {/* Toolbar */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', whiteSpace: 'nowrap' }}>To Date</label>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
              style={{ border: '1px solid #c9a84c', borderRadius: '6px', padding: '3px 8px', fontSize: '12px', height: '29px', background: '#fdf6ec', color: '#333', outline: 'none', cursor: 'pointer' }} />

            <Button variant="contained" onClick={() => { setCurrentPage(1); fetchDebtors(); }}
              style={{
                background: 'linear-gradient(135deg, #388e3c 0%, #43a047 100%)',
                color: 'white', padding: '4px 16px', borderRadius: '6px',
                fontSize: '12px', fontWeight: '700', border: 'none',
                minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(56,142,60,0.4)',
              }}>
              Refresh
            </Button>

            <Button variant="contained" onClick={handleExportExcel} disabled={filteredData.length === 0}
              style={{
                background: filteredData.length === 0 ? '#ccc' : 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                color: 'white', padding: '4px 16px', borderRadius: '6px',
                fontSize: '12px', fontWeight: '700', border: 'none',
                minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap',
                boxShadow: filteredData.length === 0 ? 'none' : '0 2px 8px rgba(21,101,192,0.4)',
              }}>
              📥 Excel
            </Button>

            <Tooltip title="Expand All">
              <IconButton size="small" onClick={expandAll} style={{ background: '#e8f5e9', borderRadius: '6px', padding: '4px' }}>
                <UnfoldMoreIcon style={{ fontSize: '18px', color: '#388e3c' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Collapse All">
              <IconButton size="small" onClick={collapseAll} style={{ background: '#fce4ec', borderRadius: '6px', padding: '4px' }}>
                <UnfoldLessIcon style={{ fontSize: '18px', color: '#c62828' }} />
              </IconButton>
            </Tooltip>

            <div style={{ flex: 1 }} />

            <TextField size="small" placeholder="Search name, bill no..." value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: '#c9a84c', fontSize: '18px' }} /></InputAdornment> }}
              sx={{ minWidth: '200px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fdf6ec', fontSize: '13px', height: '29px', '&:hover fieldset': { borderColor: '#c9a84c' }, '&.Mui-focused fieldset': { borderColor: '#c9a84c' } } }} />
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <style>{`
              .debtors-scroll::-webkit-scrollbar { height: 10px; width: 8px; }
              .debtors-scroll::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
              .debtors-scroll::-webkit-scrollbar-thumb { background: #c9a84c; border-radius: 4px; }
              .debtors-scroll::-webkit-scrollbar-thumb:hover { background: #5c3d1e; }
              .debtors-scroll { scrollbar-width: thin; scrollbar-color: #c9a84c #f1f1f1; }
            `}</style>
            <div className="debtors-scroll" style={{ overflowX: 'scroll', overflowY: 'auto', maxHeight: 'calc(100vh - 260px)', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: 0, overflow: 'visible' }}>
                <Table stickyHeader sx={{ minWidth: 700 }} aria-label="debtors table">
                  <TableHead>
                    <TableRow style={{ height: '27px' }}>
                      <TableCell style={{ ...headerCell('center'), width: '40px' }}>#</TableCell>
                      <TableCell style={headerCell()}>Customer</TableCell>
                      <TableCell style={headerCell()}>Bill No</TableCell>
                      <TableCell style={headerCell()}>Bill Date</TableCell>
                      <TableCell style={headerCell('right')}>Amount</TableCell>
                      <TableCell style={headerCell('center')}>Days</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedGroups.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} style={{ textAlign: 'center', padding: '24px', color: '#999', fontSize: '13px' }}>
                          {loading ? 'Loading...' : 'No records found.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedGroups.map((group, groupIndex) => {
                        const isExpanded = expandedCustomers[group.name];
                        const globalIndex = (currentPage - 1) * itemsPerPage + groupIndex + 1;
                        const daysColor = group.maxDays > 60 ? '#c62828' : group.maxDays > 30 ? '#e65100' : '#2e7d32';

                        return (
                          <React.Fragment key={group.name}>
                            {/* Customer Header Row */}
                            <TableRow
                              onClick={() => toggleCustomer(group.name)}
                              style={{
                                background: 'linear-gradient(135deg, #fdf6ec 0%, #fff8ee 100%)',
                                cursor: 'pointer',
                                borderBottom: '1px solid #e0d4b8',
                                transition: 'background 0.2s',
                              }}
                            >
                              <TableCell style={{ ...bodyCell('center'), color: '#5c3d1e', fontWeight: '700', fontSize: '12px', padding: '8px 10px' }}>
                                {globalIndex}
                              </TableCell>
                              <TableCell style={{ ...bodyCell(), fontWeight: '700', color: '#5c3d1e', fontSize: '13px', padding: '8px 10px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  {isExpanded
                                    ? <ExpandLessIcon style={{ fontSize: '16px', color: '#5c3d1e' }} />
                                    : <ExpandMoreIcon style={{ fontSize: '16px', color: '#5c3d1e' }} />
                                  }
                                  {group.name}
                                  <span style={{
                                    fontSize: '10px', background: '#e8eaf6', color: '#3949ab',
                                    padding: '1px 6px', borderRadius: '8px', fontWeight: '600', marginLeft: '4px'
                                  }}>
                                    {group.items.length} {group.items.length === 1 ? 'bill' : 'bills'}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell style={bodyCell()} />
                              <TableCell style={bodyCell()} />
                              <TableCell style={{ ...bodyCell('right', true), color: '#bf360c', fontSize: '13px', padding: '8px 10px' }}>
                                {fmt(group.totalAmount)}
                              </TableCell>
                              <TableCell style={{ ...bodyCell('center', true), color: daysColor, fontSize: '12px', padding: '8px 10px' }}>
                                {group.maxDays}
                              </TableCell>
                            </TableRow>

                            {/* Bill Detail Rows (visible when expanded) */}
                            {isExpanded && group.items.map((item, idx) => {
                              const rowBg = idx % 2 === 0 ? '#ffffff' : '#fafafa';
                              const itemDays = item.days || 0;
                              const itemDaysColor = itemDays > 60 ? '#c62828' : itemDays > 30 ? '#e65100' : '#2e7d32';

                              return (
                                <TableRow key={`${group.name}-${idx}`} style={{ background: rowBg, borderBottom: '1px solid #f5f5f5' }}>
                                  <TableCell style={{ ...bodyCell('center'), color: '#bbb', fontSize: '11px', paddingLeft: '20px' }} />
                                  <TableCell style={{ ...bodyCell(), paddingLeft: '38px', color: '#666', fontSize: '11px' }}>
                                    ↳
                                  </TableCell>
                                  <TableCell style={{ ...bodyCell('left', true), color: '#5c3d1e', fontSize: '12px' }}>
                                    {item.Billno || '—'}
                                  </TableCell>
                                  <TableCell style={{ ...bodyCell(), fontSize: '12px' }}>
                                    {formatDate(item.billdate)}
                                  </TableCell>
                                  <TableCell style={{ ...bodyCell('right', true), color: '#1565c0', fontSize: '12px' }}>
                                    {fmt(item.amount)}
                                  </TableCell>
                                  <TableCell style={{ ...bodyCell('center', true), color: itemDaysColor, fontSize: '12px' }}>
                                    {itemDays}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </React.Fragment>
                        );
                      })
                    )}
                    {/* Grand Total Row */}
                    {filteredData.length > 0 && (
                      <TableRow style={{ background: '#fff3e0', borderTop: '2px solid #e65100' }}>
                        <TableCell style={{ fontSize: '13px', padding: '8px 10px', fontWeight: '800', color: '#bf360c', textAlign: 'center' }}>
                          {totalCustomers}
                        </TableCell>
                        <TableCell colSpan={3} style={{ fontSize: '13px', padding: '8px 10px', fontWeight: '800', color: '#bf360c', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Grand Total ({totalBills} bills from {totalCustomers} customers)
                        </TableCell>
                        <TableCell style={{ fontSize: '13px', padding: '8px 10px', fontWeight: '800', color: '#bf360c', textAlign: 'right' }}>
                          {fmt(totalAmount)}
                        </TableCell>
                        <TableCell style={{ fontSize: '13px', padding: '8px 10px', fontWeight: '800', color: '#bf360c', textAlign: 'center' }}>—</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </div>

          <Pagination
            currentPage={currentPage} totalPages={totalPages}
            totalItems={totalCustomers} itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={n => { setItemsPerPage(n); setCurrentPage(1); }}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>
      </div>
    </Fade>
  );
}

export default DebtorsListReport;
